/**
 * Dashboard Preview Panel.
 * Displays dashboard plan before generation.
 */

import React from 'react';
import {
  Card,
  CardHeader,
  CardPreview,
  Text,
  Badge,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from '@fluentui/react-components';
import {
  ChartMultipleRegular,
  CalculatorRegular,
  ArrowRightRegular,
} from '@fluentui/react-icons';
import type { DashboardPlan } from '@excel-ai-studio/shared';
import './DashboardPreviewPanel.css';

interface DashboardPreviewPanelProps {
  readonly plan: DashboardPlan;
  readonly onGenerate?: () => void;
  readonly isGenerating?: boolean;
}

export const DashboardPreviewPanel: React.FC<DashboardPreviewPanelProps> = ({
  plan,
  onGenerate,
  isGenerating,
}) => {
  return (
    <div className="dashboard-preview-panel">
      {/* Header */}
      <Card className="preview-header-card">
        <CardHeader
          header={
            <div className="preview-header">
              <div className="preview-title">
                <Text weight="semibold" size={500}>
                  {plan.title}
                </Text>
                <Text size={300}>
                  {plan.description}
                </Text>
              </div>
              <Badge appearance="outline" size="large">
                {Math.round(plan.qualityScore)}% qualité
              </Badge>
            </div>
          }
        />
      </Card>

      {/* KPIs Preview */}
      <Card className="preview-section-card">
        <CardHeader
          header={
            <div className="section-header">
              <CalculatorRegular />
              <Text weight="semibold" size={400}>
                KPIs ({plan.kpis.length})
              </Text>
            </div>
          }
        />
        <CardPreview>
          {plan.kpis.length === 0 ? (
            <Text size={300}>Aucun KPI détecté</Text>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>Titre</TableHeaderCell>
                  <TableHeaderCell>Colonne</TableHeaderCell>
                  <TableHeaderCell>Calcul</TableHeaderCell>
                  <TableHeaderCell>Priorité</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {plan.kpis.map((kpi) => (
                  <TableRow key={kpi.id}>
                    <TableCell>{kpi.title}</TableCell>
                    <TableCell>{kpi.column}</TableCell>
                    <TableCell>
                      <Badge appearance="outline" size="small">
                        {kpi.calculation}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge
                        appearance="outline"
                        size="small"
                        color={kpi.priority === 'high' ? 'success' : kpi.priority === 'medium' ? 'warning' : 'subtle'}
                      >
                        {kpi.priority}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardPreview>
      </Card>

      {/* Charts Preview */}
      <Card className="preview-section-card">
        <CardHeader
          header={
            <div className="section-header">
              <ChartMultipleRegular />
              <Text weight="semibold" size={400}>
                Graphiques ({plan.charts.length})
              </Text>
            </div>
          }
        />
        <CardPreview>
          {plan.charts.length === 0 ? (
            <Text size={300}>Aucun graphique suggéré</Text>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>Titre</TableHeaderCell>
                  <TableHeaderCell>Type</TableHeaderCell>
                  <TableHeaderCell>Colonnes</TableHeaderCell>
                  <TableHeaderCell>Priorité</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {plan.charts.map((chart) => (
                  <TableRow key={chart.id}>
                    <TableCell>{chart.title}</TableCell>
                    <TableCell>
                      <Badge appearance="outline" size="small">
                        {chart.type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Text size={300}>
                        {chart.xColumn} × {chart.yColumn}
                      </Text>
                    </TableCell>
                    <TableCell>
                      <Badge
                        appearance="outline"
                        size="small"
                        color={chart.priority === 'high' ? 'success' : chart.priority === 'medium' ? 'warning' : 'subtle'}
                      >
                        {chart.priority}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardPreview>
      </Card>

      {/* Action Button */}
      <div className="preview-actions">
        <Button
          appearance="primary"
          size="large"
          icon={<ArrowRightRegular />}
          onClick={onGenerate}
          disabled={isGenerating || (plan.kpis.length === 0 && plan.charts.length === 0)}
        >
          {isGenerating ? 'Génération en cours...' : 'Créer le Dashboard'}
        </Button>
      </div>
    </div>
  );
};
